// Trust Center et sécurité.
// Règle absolue : rien n'est affirmé ici qui ne soit vérifiable dans le code
// ou dans la configuration. Les manques sont listés aussi clairement que les acquis.

export const POSTURE = {
  updated: "16 septembre 2026",
  summary:
    "Perform est un service jeune. Nous préférons décrire précisément ce qui est en place et ce qui ne l'est pas encore, plutôt que d'afficher des badges que nous n'avons pas obtenus.",
};

// Ce qui est réellement implémenté, vérifiable dans le dépôt.
export const CONTROLS = [
  {
    group: "Authentification",
    id: "authentification",
    items: [
      { title: "Aucun mot de passe", state: "ok", text: "La connexion se fait par lien à usage unique envoyé par e-mail. Il n'existe aucun mot de passe à voler, à deviner ou à réutiliser ailleurs." },
      { title: "Liens à usage unique", state: "ok", text: "Chaque lien expire au bout de 15 minutes et devient inutilisable dès la première ouverture." },
      { title: "Sessions opaques", state: "ok", text: "Le jeton de session est aléatoire sur 32 octets. La base ne stocke que son empreinte SHA-256 : une copie de la base ne permet de rejouer aucune session." },
      { title: "Cookie inaccessible au JavaScript", state: "ok", text: "HttpOnly, Secure et SameSite. Une injection de script ne peut pas lire la session." },
      { title: "Expiration automatique", state: "ok", text: "Index TTL en base : les sessions de plus de 30 jours et les liens périmés sont supprimés sans intervention." },
      { title: "Double facteur", state: "todo", text: "Non disponible. La sécurité du compte repose aujourd'hui sur celle de votre boîte mail." },
      { title: "SSO, SAML, SCIM", state: "todo", text: "Non disponible. Prévu pour les offres destinées aux clubs et structures." },
    ],
  },
  {
    group: "Chiffrement",
    id: "chiffrement",
    items: [
      { title: "TLS 1.3 en transit", state: "ok", text: "Tout le trafic est chiffré. HSTS est actif avec une durée d'un an, sous-domaines inclus." },
      { title: "Redirection HTTPS", state: "ok", text: "Le trafic HTTP est redirigé en 301 vers HTTPS, sur l'application comme sur l'API." },
      { title: "Chiffrement au repos", state: "partial", text: "Assuré par le volume de notre hébergeur. Nous n'avons pas encore obtenu d'attestation écrite de Railway sur ce point : nous ne le présentons donc pas comme un engagement contractuel." },
    ],
  },
  {
    group: "Application",
    id: "application",
    items: [
      { title: "Protection CSRF", state: "ok", text: "Toute requête modifiante exige un en-tête Origin appartenant à une liste fermée. Sinon, la requête est rejetée en 403." },
      { title: "En-têtes de sécurité", state: "ok", text: "CSP stricte, X-Frame-Options DENY, X-Content-Type-Options nosniff, Referrer-Policy et Permissions-Policy sur toutes les réponses." },
      { title: "Limitation de débit", state: "ok", text: "5 demandes de lien par minute et par IP, 20 vérifications par minute. L'adresse réelle est lue derrière le proxy." },
      { title: "Cloisonnement des routes", state: "ok", text: "Les pages privées sont gardées côté client et chaque endpoint revérifie la session côté serveur. L'administration exige le rôle correspondant." },
      { title: "Schéma d'API masqué", state: "ok", text: "La documentation interactive OpenAPI est désactivée en production." },
      { title: "Journaux d'audit", state: "todo", text: "Non disponible. Nous enregistrons les journaux techniques, pas encore un historique d'actions consultable par l'utilisateur." },
    ],
  },
  {
    group: "Infrastructure",
    id: "infrastructure",
    items: [
      { title: "Conteneur non privilégié", state: "ok", text: "L'API tourne sous un utilisateur dédié sans droits root." },
      { title: "Secrets hors du code", state: "ok", text: "Aucune clé n'est versionnée. Les variables sensibles vivent dans l'environnement de déploiement." },
      { title: "Sonde de santé", state: "ok", text: "Un point d'entrée public vérifie l'API et la base. Il alimente notre page d'état." },
      { title: "Sauvegardes", state: "todo", text: "Aucune sauvegarde automatique n'est configurée à ce jour. C'est notre priorité d'infrastructure immédiate." },
      { title: "Supervision et astreinte", state: "todo", text: "Pas de supervision continue ni d'astreinte 24/7. Les incidents sont traités en heures ouvrées." },
    ],
  },
  {
    group: "Données",
    id: "donnees",
    items: [
      { title: "Collecte minimale", state: "ok", text: "E-mail, pseudo, nom, photo, jeu, palier et objectif. Rien d'autre." },
      { title: "Aucune donnée bancaire", state: "ok", text: "Les paiements ne sont pas activés. Nous ne détenons aucune coordonnée bancaire." },
      { title: "Aucun traceur publicitaire", state: "ok", text: "Pas de régie, pas de pixel, pas d'analytique tierce." },
      { title: "Hébergement hors UE", state: "partial", text: "Les données sont hébergées aux États-Unis chez Railway. Le transfert doit être encadré par des clauses contractuelles types : la formalisation est en cours." },
    ],
  },
];

// Statut réel des référentiels. Aucune certification n'est revendiquée.
export const COMPLIANCE = [
  { name: "RGPD", state: "applicable", text: "Applicable. Les droits d'accès, de rectification, d'effacement et de portabilité sont exerçables par e-mail, avec réponse sous un mois." },
  { name: "SOC 2", state: "none", text: "Non certifié. Aucun audit engagé à ce jour." },
  { name: "ISO 27001", state: "none", text: "Non certifié. Aucun audit engagé à ce jour." },
  { name: "HIPAA", state: "na", text: "Sans objet : Perform ne traite aucune donnée de santé." },
  { name: "PCI DSS", state: "na", text: "Sans objet : aucun paiement n'est traité et aucune donnée de carte n'est collectée." },
  { name: "Test d'intrusion", state: "none", text: "Aucun test d'intrusion externe n'a encore été mené." },
];

export const DOCUMENTS = [
  { title: "Politique de confidentialité", text: "Données collectées, finalités, durées et droits.", to: "/legal/confidentialite", available: true },
  { title: "Accord de traitement des données", text: "Pour les clubs et structures, au sens de l'article 28 du RGPD.", to: "/legal/dpa", available: true },
  { title: "Liste des sous-traitants", text: "Chaque prestataire, sa finalité et sa localisation.", to: "/legal/sous-traitants", available: true },
  { title: "Charte d'usage acceptable", text: "Ce qui est interdit sur la plateforme.", to: "/legal/usage-acceptable", available: true },
  { title: "Rapport SOC 2", text: "Indisponible : aucune certification en cours.", available: false },
  { title: "Rapport de test d'intrusion", text: "Indisponible : aucun test réalisé à ce jour.", available: false },
];

export const SUBPROCESSORS = {
  updated: "16 septembre 2026",
  rows: [
    { provider: "Railway Corp.", purpose: "Hébergement de l'application, de l'API et de la base", data: "Toutes les données du service", location: "États-Unis", country: "US" },
    { provider: "MongoDB (via Railway)", purpose: "Base de données", data: "Comptes, sessions, profils", location: "États-Unis", country: "US" },
    { provider: "Resend", purpose: "Envoi des liens de connexion", data: "Adresse e-mail", location: "États-Unis", country: "US" },
    { provider: "Cloudflare, Inc.", purpose: "DNS du domaine", data: "Aucune donnée personnelle (résolution DNS uniquement)", location: "États-Unis", country: "US" },
    { provider: "Google Fonts", purpose: "Polices de caractères", data: "Adresse IP lors du chargement des polices", location: "États-Unis", country: "US" },
  ],
};

export const AI_TRANSPARENCY = {
  state: "Aucune fonctionnalité d'IA n'est active à ce jour.",
  points: [
    { title: "Quels modèles ?", text: "Aucun. Aucune requête n'est envoyée à un fournisseur de modèle depuis l'application en production." },
    { title: "Quelles données seraient envoyées ?", text: "À l'activation, chaque fonctionnalité affichera précisément les données transmises, avant tout envoi." },
    { title: "Entraînement", text: "Vos données ne serviront jamais à entraîner un modèle, ni le nôtre ni celui d'un tiers. C'est un engagement contractuel, pas une option." },
    { title: "Conservation", text: "Les échanges avec un modèle ne seront pas conservés au-delà du temps nécessaire à l'affichage du résultat." },
    { title: "Contrôle", text: "Un retrait sera possible sans perdre l'accès au reste du service." },
    { title: "Revue humaine", text: "Aucune décision produisant un effet juridique ne sera prise automatiquement. Une suggestion reste une suggestion." },
  ],
};

export const DISCLOSURE = {
  email: "securite@performcustom.com",
  sla: "Accusé de réception sous 72 heures, première évaluation sous 7 jours.",
  inScope: [
    "performcustom.com et www.performcustom.com",
    "api.performcustom.com",
    "L'application web et son API publique",
  ],
  outOfScope: [
    "Déni de service, volumétrie et tests de charge",
    "Ingénierie sociale visant nos utilisateurs ou notre équipe",
    "Failles sur des services tiers (Railway, Cloudflare, Resend) — à signaler directement chez eux",
    "Absence d'en-tête sans impact démontré, versions de bibliothèques sans exploitation",
  ],
  rules: [
    "Ne consultez, ne modifiez et ne supprimez aucune donnée qui ne vous appartient pas.",
    "Utilisez un compte de test que vous créez vous-même.",
    "Ne divulguez rien publiquement avant que le correctif soit déployé.",
    "Donnez-nous un délai raisonnable : 90 jours sauf faille activement exploitée.",
  ],
  include: [
    "Une description claire et les étapes exactes de reproduction",
    "L'URL, le point d'entrée et la méthode HTTP concernés",
    "L'impact que vous estimez, avec un exemple concret",
    "Une capture ou une trace si elle aide à comprendre",
  ],
};
